/**
 * Attendance day-status system — the single source of truth for how a day's
 * status is RESOLVED (precedence), LABELLED and COLOURED across every
 * timesheet view (admin monthly/yearly, my monthly/yearly, exports).
 *
 * Data contract (backend `UserAttendance.entries`): one date can carry
 * SEVERAL entries — e.g. a holiday that falls on a weekend, or a check-in on
 * a holiday. Rendering must therefore pick ONE status per day by priority:
 *
 *   actual work (present/late/remote)  >  leave  >  holiday  >  weekend
 *
 * Days with no entry at all are "absent" ONLY when they are working days in
 * the past/today — future days are "future" (upcoming), and weekend/holiday
 * days always arrive as entries from the backend.
 *
 * Colours: hand-tuned per theme mode for WCAG-friendly contrast between the
 * cell background and the day number —
 *   light mode: soft tinted backgrounds + deep saturated text
 *   dark mode:  deep muted backgrounds + light text
 * Semantic hues stay constant across modes: present=green · late=orange ·
 * leave=gold · holiday=blue · remote=slate · absent=red · weekend=neutral.
 */
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTheme } from '@mui/material';

/* ------------------------------------------------------------------ */
/* Status metadata                                                     */
/* ------------------------------------------------------------------ */
export const DAY_STATUS = {
    in: { label: 'On site', priority: 1 },
    remote: { label: 'Remote', priority: 1 },
    arrivedLate: { label: 'Late', priority: 1 },
    leave: { label: 'Leave', priority: 2 },
    holiday: { label: 'Holiday', priority: 3 },
    weekend: { label: 'Weekend', priority: 4 },
    absent: { label: 'Absent', priority: 5 },
    future: { label: 'Upcoming', priority: 6 },
    /** Before the joining date or after the resignation date. */
    notEmployed: { label: 'Not Employed', priority: 7 }
};

/** Order used by legends and summary chips. */
export const LEGEND_STATUSES = [
    'in',
    'remote',
    'arrivedLate',
    'leave',
    'holiday',
    'weekend',
    'absent'
];

/**
 * WCAG-friendly bg/text pairs per theme mode (day-number contrast ≥ 4.5:1).
 * Light mode: soft tints + deep text · dark mode: deep tones + light text.
 */
const LIGHT_STATUS_COLORS = {
    in: { bg: '#9FE0B4', text: '#0A5229' },
    // Remote is "present, but elsewhere" — a teal-leaning green, sibling of
    // the Present green rather than a foreign hue.
    remote: { bg: '#93DCCB', text: '#065546' },
    arrivedLate: { bg: '#FFCE8F', text: '#733B00' },
    leave: { bg: '#EFDD7E', text: '#544700' },
    holiday: { bg: '#A5CBF5', text: '#123C73' },
    weekend: { bg: '#DFDFE6', text: '#585863' },
    absent: { bg: '#F2A9A9', text: '#7E1212' }
};

const DARK_STATUS_COLORS = {
    in: { bg: '#1E4D32', text: '#9FE5B8' },
    // Teal-leaning green — sibling of the Present green (see light map).
    remote: { bg: '#155048', text: '#8EE0CF' },
    arrivedLate: { bg: '#5E3A10', text: '#F6C083' },
    leave: { bg: '#54480F', text: '#EBD97C' },
    holiday: { bg: '#1E3B5E', text: '#9DC5F2' },
    weekend: { bg: '#3A3A42', text: '#ABABB6' },
    absent: { bg: '#5C1F1F', text: '#F2A6A6' }
};

/** Background + text colour for a status square/chip, theme-aware. */
export const statusVisual = (palette, statusKey) => {
    const dark = palette.mode === 'dark';
    const colors = dark ? DARK_STATUS_COLORS : LIGHT_STATUS_COLORS;
    if (colors[statusKey]) return colors[statusKey];
    // 'future' and anything unknown: barely-there placeholder
    return {
        bg: dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
        text: palette.text.disabled
    };
};

export const statusLabel = (statusKey) =>
    DAY_STATUS[statusKey]?.label || 'Unknown';

/** Hook: background colour for a status (used by day squares/legends). */
export const useStatusVisual = (statusKey) => {
    const { palette } = useTheme();
    return statusVisual(palette, statusKey);
};

/* ------------------------------------------------------------------ */
/* Per-day resolution                                                  */
/* ------------------------------------------------------------------ */
const entryStatusKey = (entry) => {
    let key = entry?.presentType || entry?.status;
    // Backend flags remote check-ins but keeps status "in"; surface it —
    // late-from-remote stays "late" (the more important signal).
    if (key === 'in' && entry?.isFromRemote) key = 'remote';
    return key;
};

/**
 * Resolve ONE status for a date from all of its entries.
 * Returns { status, entry, extras } — `entry` is the winning entry (carries
 * in/out/duration/leaveType/label), `extras` the other statuses that also
 * apply to the day (e.g. worked on a holiday → extras: ['holiday']).
 *
 * `bounds` = { joiningDate, resignationDate }: an entry-less day only counts
 * as ABSENT when it is a past/current day INSIDE the employment window —
 * days before joining, after resignation or in the future are never absent.
 */
export const resolveDayStatus = (entriesForDay, isoDate, today, bounds = {}) => {
    const items = (entriesForDay || [])
        .map((entry) => ({ key: entryStatusKey(entry), entry }))
        .filter((item) => DAY_STATUS[item.key]);

    if (!items.length) {
        const day = dayjs(isoDate);
        if (day.isAfter(today || dayjs(), 'day')) {
            return { status: 'future', entry: null, extras: [] };
        }
        const beforeJoining =
            bounds.joiningDate &&
            day.isBefore(dayjs(bounds.joiningDate), 'day');
        const afterResignation =
            bounds.resignationDate &&
            day.isAfter(dayjs(bounds.resignationDate), 'day');
        if (beforeJoining || afterResignation) {
            return { status: 'notEmployed', entry: null, extras: [] };
        }
        return { status: 'absent', entry: null, extras: [] };
    }

    items.sort(
        (a, b) => DAY_STATUS[a.key].priority - DAY_STATUS[b.key].priority
    );
    const [best, ...rest] = items;
    const extras = [
        ...new Set(rest.map((item) => item.key).filter((k) => k !== best.key))
    ];
    return { status: best.key, entry: best.entry, extras };
};

/* ------------------------------------------------------------------ */
/* Month day-sheet                                                     */
/* ------------------------------------------------------------------ */
const dateKeyOf = (entry) =>
    typeof entry?.date === 'string'
        ? entry.date.slice(0, 10)
        : entry?.date
        ? dayjs(entry.date).format('YYYY-MM-DD')
        : '';

/**
 * Build one resolved record per day of the month (pure — no hooks).
 * Keeps the legacy fields older components rely on (`date` formatted,
 * `presentType`, `duration`, `inTime`, `outTime`) and adds the resolved ones.
 */
/**
 * A leave's share of the working day, as a number that is safe to add.
 *
 * The API sends 1, 0.75, 0.5 or 0.25. Anything absent, non-numeric or out of
 * range reads as a whole day, which is how every payload behaved before the
 * portion existed — so historical exports are unchanged.
 */
export const normalizeLeavePortion = (value) => {
    const portion = Number(value);
    if (!Number.isFinite(portion)) return 1;
    if (portion <= 0 || portion > 1) return 1;
    return portion;
};

export const buildMonthDaySheet = (entries, yearMonth, bounds = {}) => {
    const monthDate = dayjs(yearMonth);
    if (!monthDate.isValid()) return [];

    const byDate = {};
    (entries || []).forEach((entry) => {
        const key = dateKeyOf(entry);
        if (!key) return;
        (byDate[key] = byDate[key] || []).push(entry);
    });

    const today = dayjs();
    const daysInMonth = monthDate.daysInMonth();
    const sheet = [];
    for (let dayNo = 1; dayNo <= daysInMonth; dayNo += 1) {
        const dayDate = monthDate.date(dayNo);
        const isoDate = dayDate.format('YYYY-MM-DD');
        const { status, entry, extras } = resolveDayStatus(
            byDate[isoDate],
            isoDate,
            today,
            bounds
        );
        sheet.push({
            // legacy fields (existing tooltips/components)
            date: dayDate.format('dddd DD MMM YYYY'),
            presentType: status,
            duration: entry?.duration || 0,
            inTime: entry?.inTime || null,
            outTime: entry?.outTime || null,
            // resolved fields
            isoDate,
            dayOfMonth: dayNo,
            weekday: dayDate.day(),
            status,
            statusLabel: statusLabel(status),
            extras,
            leaveType: entry?.leaveType || null,
            // A missing portion means a payload from before the API carried
            // one, and those were all whole days.
            leavePortion: normalizeLeavePortion(entry?.leavePortion),
            label: entry?.label || null,
            isFromRemote: entry?.isFromRemote || false
        });
    }
    return sheet;
};

/** Memoised hook flavour of buildMonthDaySheet. */
export const useMonthDaySheet = (entries, yearMonth, bounds) =>
    useMemo(
        () => buildMonthDaySheet(entries, yearMonth, bounds),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            entries,
            yearMonth,
            bounds?.joiningDate,
            bounds?.resignationDate
        ]
    );

/* ------------------------------------------------------------------ */
/* Summaries (per-day — deduped, unlike raw entry counting)            */
/* ------------------------------------------------------------------ */
const LEAVE_TYPE_BUCKETS = [
    { key: 'casualLeave', match: (t) => t === 'Casual' },
    { key: 'sickLeave', match: (t) => t === 'Sick' },
    {
        key: 'maternityPaternityLeave',
        match: (t) => t === 'Maternity' || t === 'Paternity'
    },
    { key: 'compensatoryLeave', match: (t) => t === 'Compensatory Leave' }
];

/** Per the user's calendar, is this day a holiday or weekend? A check-in on
 *  a holiday resolves to a present status, but the calendar day itself stays
 *  non-working — hence the `extras` check. */
const isNonWorkingDay = (day) =>
    day.status === 'holiday' ||
    day.status === 'weekend' ||
    (day.extras || []).includes('holiday') ||
    (day.extras || []).includes('weekend');

/**
 * Count the resolved statuses of a day sheet (or several concatenated
 * sheets). One count per DAY — a holiday on a weekend is one holiday,
 * not one of each. Also provides the derived metrics:
 *   totalPresent = On site + Remote + Late
 *   workingDays  = every day except the calendar's holidays & weekends
 *   lateOnsite / lateRemote = Late split by where the check-in happened
 */
export const summarizeDays = (daySheet) => {
    const summary = {
        present: 0,
        remote: 0,
        arrivedLate: 0,
        lateOnsite: 0,
        lateRemote: 0,
        leave: 0,
        casualLeave: 0,
        sickLeave: 0,
        maternityPaternityLeave: 0,
        compensatoryLeave: 0,
        otherLeave: 0,
        holiday: 0,
        weekend: 0,
        absent: 0,
        future: 0,
        notEmployed: 0,
        totalPresent: 0,
        workingDays: 0
    };
    (daySheet || []).forEach((day) => {
        if (!isNonWorkingDay(day)) summary.workingDays += 1;
        switch (day.status) {
            case 'in':
                summary.present += 1;
                break;
            case 'remote':
                summary.remote += 1;
                break;
            case 'arrivedLate':
                summary.arrivedLate += 1;
                if (day.isFromRemote) summary.lateRemote += 1;
                else summary.lateOnsite += 1;
                break;
            case 'leave': {
                // Sum the portion, never the day. A half day counted as 1
                // overstates absence on every report HR reads.
                const portion = normalizeLeavePortion(day.leavePortion);
                summary.leave += portion;
                const bucket = LEAVE_TYPE_BUCKETS.find((b) =>
                    b.match(day.leaveType)
                );
                summary[bucket ? bucket.key : 'otherLeave'] += portion;
                break;
            }
            case 'holiday':
                summary.holiday += 1;
                break;
            case 'weekend':
                summary.weekend += 1;
                break;
            case 'absent':
                summary.absent += 1;
                break;
            case 'future':
                summary.future += 1;
                break;
            case 'notEmployed':
                summary.notEmployed += 1;
                break;
            default:
                break;
        }
    });
    summary.totalPresent =
        summary.present + summary.remote + summary.arrivedLate;
    return summary;
};
